import { StageVariant } from "@/game/stages/StageData";

/**
 * フロアの進捗状態
 */
export type FloorStatus = "pending" | "running" | "cleared" | "failed";

/**
 * 階層ダンジョンのフロア切り替えタブUI
 *
 * ゲームキャンバスの上に配置し、各フロアの選択と進捗表示を行う。
 * 単一フロアステージでは非表示。
 */
export class FloorTabs {
  private container: HTMLElement;
  private floors: StageVariant[] = [];
  private activeFloorIndex = 0;
  private statuses: FloorStatus[] = [];
  private interactive = true;
  private selectCallback?: (index: number) => void;

  constructor(parentElement: HTMLElement, insertBefore?: HTMLElement) {
    this.container = document.createElement("div");
    this.container.className = "floor-tabs floor-tabs--hidden";
    this.container.setAttribute("role", "tablist");
    this.container.setAttribute("aria-label", "フロア選択");
    if (insertBefore) {
      parentElement.insertBefore(this.container, insertBefore);
    } else {
      parentElement.appendChild(this.container);
    }
  }

  /**
   * コンテナ要素を返す（DOM配置用）
   */
  getElement(): HTMLElement {
    return this.container;
  }

  /**
   * フロアデータを設定しタブを生成
   */
  setFloors(floors: StageVariant[]): void {
    this.floors = floors;
    this.activeFloorIndex = 0;
    this.statuses = floors.map(() => "pending" as FloorStatus);
    this.render();
  }

  /**
   * タブを非表示にする（単一フロアステージ用）
   */
  hide(): void {
    this.container.classList.add("floor-tabs--hidden");
  }

  /**
   * タブを表示する
   */
  show(): void {
    this.container.classList.remove("floor-tabs--hidden");
  }

  /**
   * 選択中のフロアを更新
   */
  setActiveFloor(index: number): void {
    if (index < 0 || index >= this.floors.length) return;
    this.activeFloorIndex = index;
    this.render();
  }

  /**
   * フロアの進捗状態を更新
   */
  setFloorStatus(index: number, status: FloorStatus): void {
    if (index < 0 || index >= this.statuses.length) return;
    this.statuses[index] = status;
    this.render();
  }

  /**
   * 全フロアの状態をリセット
   */
  resetStatus(): void {
    this.statuses = this.statuses.map(() => "pending");
    this.activeFloorIndex = 0;
    this.render();
  }

  /**
   * フロア選択時のコールバック
   */
  onFloorSelect(callback: (index: number) => void): void {
    this.selectCallback = callback;
  }

  /**
   * タブのクリック操作を有効/無効にする（実行中は無効）
   */
  setInteractive(interactive: boolean): void {
    this.interactive = interactive;
    this.render();
  }

  /**
   * タブUIを描画
   */
  private render(): void {
    const fragment = document.createDocumentFragment();
    this.floors.forEach((_, index) => {
      const tab = this.createTab(index);
      fragment.appendChild(tab);
    });
    this.container.replaceChildren(fragment);
  }

  /**
   * 個別のタブボタンを作成
   */
  private createTab(index: number): HTMLButtonElement {
    const tab = document.createElement("button");
    tab.className = "floor-tab";
    tab.setAttribute("role", "tab");
    tab.setAttribute("aria-label", `${index + 1}階`);
    tab.setAttribute("aria-selected", String(index === this.activeFloorIndex));
    tab.textContent = `${index + 1}階`;

    // 選択状態
    if (index === this.activeFloorIndex) {
      tab.classList.add("floor-tab--selected");
    }

    // 進捗状態
    const status = this.statuses[index];
    if (status !== "pending") {
      tab.classList.add(`floor-tab--${status}`);
    }

    // インタラクティブ制御
    if (!this.interactive) {
      tab.disabled = true;
      tab.classList.add("floor-tab--disabled");
    }

    tab.addEventListener("click", () => {
      this.activeFloorIndex = index;
      this.render();
      this.selectCallback?.(index);
    });

    return tab;
  }
}
